import { useFormContext } from "react-hook-form";
import CommentIcon from "@/assets/icons/comment.svg?react";
import { GetAllCompanyDto, PickUpLocationFullCompanyDto } from "@/types/api/api-types";
import { Paperclip, Plus } from "lucide-react";
import { DeliveryFormOcrData } from "@/types/forms/home/cotationDeliveryForm.form";
import { useState } from "react";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { Button } from "@/components/ui/button";
import { UploadFileClientModal } from "@/components/backOffice/expeditionForm/uploadFileModal";
import {
  ExpeditionDocumentCard,
  NewFileDocumentCard,
} from "@/components/backOffice/expeditionForm/expeditionDocumentCard";
import { ExpeditionDocumentFileDto } from "@/types/api/api-types";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import {
  FileAction,
  getCreatableFileTypes,
  getVisibleFileTypes,
  sortDocumentsByPriority,
} from "@/constants/fileCrud";
import { Trans, useTranslation } from "react-i18next";

interface InfoClientsProps {
  pickUpLocations: PickUpLocationFullCompanyDto[];
  billingEntities: GetAllCompanyDto["billingEntities"];
  documents: ExpeditionDocumentFileDto[];
}

export const InfoDelivery = ({ billingEntities, pickUpLocations, documents }: InfoClientsProps) => {
  const { t } = useTranslation("boExpeditions");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newFiles, setNewFiles] = useState<{ fileId: string; file: File; type: FileTypeEnum }[]>(
    [],
  );
  const methods = useFormContext<DeliveryFormOcrData>();

  const newFilesId = methods.watch("newFilesId");
  const removedFilesId = methods.watch("removedFilesId");
  const readableFileTypes = getVisibleFileTypes(FileAction.FO_VALIDATE_EXPEDITION);
  const readableDocuments = sortDocumentsByPriority(
    documents.filter((document) => readableFileTypes.includes(document.type as FileTypeEnum)),
  );
  const creatableFileTypes = getCreatableFileTypes(FileAction.FO_VALIDATE_EXPEDITION);

  const handleDeleteFile = async (fileId: string) => {
    const isNewlyAdded = newFilesId?.includes(fileId);
    const isExisting = documents?.some((document) => document.id === fileId);

    if (isNewlyAdded) {
      methods.setValue("newFilesId", newFilesId?.filter((id) => id !== fileId) || []);
      setNewFiles(newFiles.filter((file) => file.fileId !== fileId));
    } else if (isExisting) {
      methods.setValue("removedFilesId", [...new Set([...(removedFilesId || []), fileId])]);
    }
  };

  const handleUploadSuccess = (fileId: string, selectedType: string, file: File) => {
    setNewFiles([...newFiles, { fileId, file, type: selectedType as FileTypeEnum }]);
    methods.setValue("newFilesId", [...(newFilesId || []), fileId]);
  };

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <div className="flex w-full flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6 md:max-w-[1000px]">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <CommentIcon />
          <h2 className="text-xm font-semibold text-black">
            {t("pendingShipmentsDeliveryInfo.commentTitle")}
          </h2>
        </div>
        <textarea
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-normal hover:bg-gray-50"
          rows={4}
          cols={50}
          placeholder={t("pendingShipmentsDeliveryInfo.commentPlaceholder")}
          {...methods.register("comment")}
        />
      </div>
      <div className="flex w-full max-w-[1000px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <Paperclip />
          <h2 className="text-xm font-semibold text-black">
            <Trans
              t={t}
              i18nKey="pendingShipmentsDeliveryInfo.attachmentsTitle"
              components={{ hint: <span className="text-sm font-normal text-gray-500" /> }}
            />
          </h2>
        </div>
        <div className="flex max-h-[70vh] w-full flex-col gap-8 md:flex">
          <div className="flex flex-col gap-2">
            {readableDocuments
              ?.filter((file: ExpeditionDocumentFileDto) => !removedFilesId?.includes(file.id))
              .map((file: ExpeditionDocumentFileDto) => (
                <ExpeditionDocumentCard
                  key={file.id}
                  file={file}
                  onDelete={handleDeleteFile}
                  hideDetailsInfo={true}
                />
              ))}
            {newFiles.map((file) => (
              <NewFileDocumentCard
                key={file.fileId}
                file={file}
                onDelete={handleDeleteFile}
                hideDetailsInfo={true}
              />
            ))}
          </div>

          <Button
            variant="outline"
            type="button"
            className="self-start"
            onClick={() => setUploadModalOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            {t("pendingShipmentsDeliveryInfo.importFile")}
          </Button>

          <UploadFileClientModal
            open={uploadModalOpen}
            submitOnDrop
            onOpenChange={setUploadModalOpen}
            allowedFileTypes={creatableFileTypes}
            uploadSuccessCallback={handleUploadSuccess}
          />
        </div>
      </div>
      <div className="shadow-basic flex w-full flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6 md:max-w-[1000px]">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <h2 className="text-brand700 text-xl font-semibold">
            {t("pendingShipmentsDeliveryInfo.validationTitle")}
          </h2>
          <div className="flex w-full flex-col gap-4">
            <ControlledSelectV2
              options={billingEntities.map((billingEntity) => ({
                value: billingEntity.id,
                label: `${billingEntity.companyName}`,
              }))}
              setValue={(e: any) => methods.setValue("billingEntityId", e)}
              label={t("pendingShipmentsDeliveryInfo.billingEntityLabel")}
              control={methods.control}
              name="billingEntityId"
              contentClassName="w-full"
            />
            <ControlledSelectV2
              options={pickUpLocations.map((pickUpLocation) => ({
                value: pickUpLocation.id,
                label: `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`,
              }))}
              setValue={(e: any) => methods.setValue("pickUpLocationId", e)}
              label={t("pendingShipmentsDeliveryInfo.pickupLocationLabel")}
              control={methods.control}
              name="pickUpLocationId"
              contentClassName="w-full"
            />
            <ControlledUnifiedInput
              label={t("pendingShipmentsDeliveryInfo.referenceLabel")}
              name="reference"
              control={methods.control}
              inputType="text"
              placeholder={t("pendingShipmentsDeliveryInfo.referencePlaceholder")}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
